import { Box, Grid, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React from "react";
import dayjs from "dayjs";
import { useAppSelector } from "../../../../hooks/hooks";
import { tokens } from "../../../../theme";
import MiniSpinner from "../../../reusableComponents/miniSpinner/miniSpinner";
import type { ContextBarProps } from "../contextBar";
import type { LegacyScreenChrome } from "./legacyScreens";
import { LEGACY_FIELD_BG, LEGACY_RAIL } from "./legacyMain";

/**
 * ── LEGACY SHELL — SCHEDULED FOR DELETION ──────────────────────────────────
 *
 * dashboardHeader.tsx's title block, restored from `frontend_refactor`: the
 * uppercase navy client name with Starting Date, Last Update and Currency
 * stacked beneath it, in that order and with those exact labels.
 *
 * Reads the store directly, as the original did, rather than parsing the joined
 * `meta` string PortalShell builds for the new context bar — that produced the
 * right values in the wrong order under the wrong labels.
 *
 * The navy is inline because the original was inline: the old app had no
 * ThemeProvider, so nothing came from a palette.
 */
const NAVY = "#004680";

const lineSx = {
  color: NAVY,
  fontWeight: "bold",
  display: "flex",
  alignItems: "center",
  gap: 2
};

interface Props extends ContextBarProps {
  /** Optional so AppShell can spread it without narrowing; defaults to the
   *  analytics screen, which is what every line-bearing screen used. */
  chrome?: LegacyScreenChrome;
  /** True once <main> has scrolled. Declared HERE and not on ContextBarProps:
   *  the other four themes already handle the pinned seam themselves, and
   *  invariant §11.1 says no shared component may be changed for legacy's
   *  benefit. AppShell passes it only on the legacy branch. */
  scrolled?: boolean;
}

const ALL_LINES: LegacyScreenChrome = {
  tint: true,
  title: true,
  startingDate: true,
  lastUpdate: true,
  currency: true,
  filters: true
};

const LegacyContextBar = ({ title, filters, chrome = ALL_LINES, scrolled }: Props) => {
  const { selectedClient, selectedLastUpdated, lastUpdateLoading } = useAppSelector(
    (state: any) => state.portalManagement
  );

  return (
    // Plain Box, not `Grid item xs={12}`: in AppShell's flex COLUMN an item's
    // xs sets flex-basis 100%, which resolves against the height and lets this
    // bar swallow the whole content area. The original sat in normal block flow,
    // where xs only ever meant width.
    <Box
      sx={{
        flex: "none",
        width: "100%",
        // The original's own 20px, unchanged — because the bar now carries the
        // field's rails below, exactly as the wrapper it used to live inside
        // did. 10px rail + 20px padding puts the title at x=30, which is where
        // the original had it. An earlier version faked that with 30px of
        // padding and no rails, which got the text position right but left a
        // band of bare canvas between the tab strip and the field.
        // dashboardHeader's own 10px 20px unless the screen's wrapper changed
        // the box it sat in — see `barPadding`.
        padding: chrome.barPadding
          ? `${chrome.barPadding.top}px ${chrome.barPadding.x}px ${chrome.barPadding.bottom}px`
          : "10px 20px 10px 20px",
        boxSizing: "border-box",
        // Only on the screens whose content sits on the tinted field. The
        // others (training grades, sales rep, the selection view) had no
        // wrapper in the original either, so the bar sits on the page.
        ...(chrome.tint
          ? {
              backgroundColor: LEGACY_FIELD_BG,
              borderLeft: LEGACY_RAIL,
              borderRight: LEGACY_RAIL
            }
          : {}),
        // DELIBERATE DEVIATION from frontend_refactor — see README. The original
        // title block scrolled away with the page, so it never needed a seam.
        // This one is pinned, and without a divider the content slides under it
        // with nothing marking the edge. Only on scroll, so at rest the screen
        // is still pixel-identical to the original.
        //
        // NOT tokens.shadow.card — that is MUI's elevation-1, three stacked
        // layers at .2/.14/.12 alpha, which reads as a raised card rather than a
        // seam. One soft wide shadow at .07, derived from tokens.color.ink via
        // alpha() so no literal colour enters a component (invariant 2).
        borderBottom: `1px solid ${
          scrolled || chrome.divider ? tokens.color.border : "transparent"
        }`,
        boxShadow: scrolled ? `0 6px 14px -6px ${alpha(tokens.color.ink, 0.07)}` : "none",
        transition: `box-shadow ${tokens.motion.base}, border-color ${tokens.motion.base}`,
        zIndex: 1
      }}
    >
      <Grid
        container
        item
        xs={12}
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Grid item xs={8}>
          <Typography variant="h4" sx={{ color: NAVY, fontWeight: "bold" }}>
            {title?.toUpperCase()}
          </Typography>
          {chrome.startingDate && (
          <Typography sx={lineSx}>
            Starting Date:{" "}
            {selectedClient?.create_date
              ? dayjs(selectedClient.create_date).format("YYYY-MM-DD")
              : ""}
          </Typography>
          )}
          {chrome.lastUpdate && selectedLastUpdated && (
            <Typography sx={lineSx}>
              Last Update : {lastUpdateLoading ? <MiniSpinner /> : selectedLastUpdated}
            </Typography>
          )}
          {chrome.currency && (
            <Typography sx={lineSx}>Currency : {selectedClient?.currency}</Typography>
          )}
        </Grid>
        {filters && (
          <Grid item xs={4} sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            {filters}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default LegacyContextBar;
